import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";

import { ScrapedBrandRepository } from "@/repositories/scraped-brand";
import { PaginatedResponse } from "@/types";
import { ScrapedBrand, UpdateScrapedBrand } from "@/types/scraped-brand";

export const useUpdateScrapedBrand = () => {
  const queryClient = useQueryClient();
  const scrapedBrandRepository = useRef(new ScrapedBrandRepository());

  return useMutation({
    mutationFn: async (data: UpdateScrapedBrand) => {
      return await scrapedBrandRepository.current.update(data);
    },
    onMutate: async (newData: UpdateScrapedBrand) => {
      const previousQueries = queryClient.getQueriesData({
        predicate: (query) =>
          query.queryKey[0] === "SCRAPED_BRAND" && query.queryKey[1] === "LIST",
      });

      queryClient.setQueriesData(
        {
          predicate: (query) =>
            query.queryKey[0] === "SCRAPED_BRAND" &&
            query.queryKey[1] === "LIST",
        },
        (oldData) => {
          if (!oldData) return oldData;
          const cachedData = oldData as PaginatedResponse<ScrapedBrand>;

          return {
            ...cachedData,
            data: cachedData.data.map((brand) =>
              brand.id === newData.id
                ? {
                    ...brand,
                    blacklist: newData.blacklist ?? brand.blacklist,
                    linkedBrandId: newData.linkedBrandId ?? brand.linkedBrandId,
                  }
                : brand,
            ),
          };
        },
      );

      return { previousQueries };
    },
    onError: (_err, _newData, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      // Invalidate scraped brands queries to refetch
      queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "SCRAPED_BRAND" && query.queryKey[1] === "LIST",
      });
    },
  });
};
